import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

const Select = forwardRef(
  (
    {
      label,
      options = [],
      value,
      onChange,
      onBlur,
      placeholder = 'Select an option',
      error,
      disabled = false,
      required = false,
      searchable = false,
      multiple = false,
      className = '',
      id,
      name,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownStyle, setDropdownStyle] = useState({});
    const selectRef = useRef(null);
    const buttonRef = useRef(null);
    const searchInputRef = useRef(null);

    const selectId =
      id || name || `select-${Math.random().toString(36).substr(2, 9)}`;

    // Filter options based on search term
    const filteredOptions = searchable
      ? options.filter(option =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : options;

    // Get selected option(s) for display
    const getSelectedDisplay = () => {
      if (multiple) {
        if (!value || value.length === 0) return placeholder;
        if (value.length === 1) {
          const option = options.find(opt => opt.value === value[0]);
          return option ? option.label : placeholder;
        }
        return `${value.length} items selected`;
      } else {
        // Handle empty string as a valid value
        if (value === undefined || value === null) return placeholder;
        const option = options.find(opt => opt.value === value);
        return option ? option.label : placeholder;
      }
    };

    // Handle option selection
    const handleOptionSelect = optionValue => {
      console.log('handleOptionSelect called with:', optionValue, 'current value:', value);
      if (multiple) {
        const newValue = value || [];
        const isSelected = newValue.includes(optionValue);
        const updatedValue = isSelected
          ? newValue.filter(v => v !== optionValue)
          : [...newValue, optionValue];
        console.log('Multiple selection - calling onChange with:', updatedValue);
        onChange(updatedValue);
      } else {
        console.log('Single selection - calling onChange with:', optionValue);
        onChange(optionValue);
        setIsOpen(false);
      }
    };

    // Handle click outside to close dropdown
    useEffect(() => {
      const handleClickOutside = event => {
        // Check if click is outside both the select trigger and the portal dropdown
        const isOutsideSelect = selectRef.current && !selectRef.current.contains(event.target);
        const isOutsideDropdown = !event.target.closest('[data-dropdown]');
        
        if (isOutsideSelect && isOutsideDropdown) {
          setIsOpen(false);
          setSearchTerm('');
          if (onBlur) onBlur();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, [onBlur]);

    // Calculate dropdown position when opening and keep it aligned on scroll/resize
    useEffect(() => {
      if (!isOpen) return;
      const updatePosition = () => {
        const triggerEl = buttonRef.current || (ref && ref.current) || selectRef.current;
        if (!triggerEl) return;
        const rect = triggerEl.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 240; // max-h-60
        const dropdownWidth = rect.width; // match trigger width

        let top = rect.bottom + 4;
        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
          top = rect.top - dropdownHeight - 4;
        }
        let left = rect.left;
        if (left + dropdownWidth > viewportWidth) left = Math.max(10, viewportWidth - dropdownWidth - 10);

        setDropdownStyle({
          position: 'fixed',
          top: `${top}px`,
          left: `${left}px`,
          width: `${dropdownWidth}px`,
          zIndex: 9999,
        });
      };
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }, [isOpen, ref]);

    // Focus search input when dropdown opens
    useEffect(() => {
      if (isOpen && searchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isOpen, searchable]);

    const baseClasses = 'relative block w-full';
    const buttonClasses = `w-full px-3 py-2 text-left bg-white border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 sm:text-sm transition-colors duration-200 ${
      error
        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
        : 'border-gray-300 focus:ring-[#fdbd0a] focus:border-[#fdbd0a]'
    } ${
      disabled
        ? 'bg-gray-50 text-gray-500 cursor-not-allowed'
        : 'cursor-pointer hover:border-gray-400'
    }`;

    // Dropdown content component
    const DropdownContent = () => (
      <div 
        className="bg-white shadow-xl max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm border border-gray-200 dropdown-menu transition-all duration-200 ease-in-out transform"
        style={{
          ...dropdownStyle,
          overflowY: 'auto',
          overflowX: 'hidden',
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)',
        }}
        data-dropdown
      >
        {searchable && (
          <div className="sticky top-0 bg-white px-3 py-2 border-b border-gray-200">
            <input
              ref={searchInputRef}
              type="text"
              className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-yellow focus:border-brand-yellow"
              placeholder="Search options..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        )}

        {filteredOptions.length === 0 ? (
          <div className="px-3 py-2 text-gray-500 text-sm">
            {searchable && searchTerm
              ? 'No options found'
              : 'No options available'}
          </div>
        ) : (
          filteredOptions.map(option => {
            const isSelected = multiple
              ? value && value.includes(option.value)
              : value === option.value;

            return (
              <div
                key={option.value}
                className={`cursor-pointer select-none relative py-1.5 pl-3 pr-9 hover:bg-brand-yellow/10 ${
                  isSelected
                    ? 'bg-brand-yellow/20 text-dark'
                    : 'text-gray-900'
                }`}
                onClick={() => handleOptionSelect(option.value)}
              >
                <div className="flex items-center">
                  {multiple && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}} // Handled by parent onClick
                      className="h-4 w-4 text-brand-yellow focus:ring-brand-yellow border-gray-300 rounded mr-3"
                    />
                  )}
                  <span
                    className={`block truncate ${isSelected ? 'font-medium' : 'font-normal'}`}
                  >
                    {option.label}
                  </span>
                </div>

                {isSelected && !multiple && (
                  <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#fdbd0a]">
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
    );

    return (
      <div className={`${baseClasses} dropdown-container ${className}`} ref={selectRef}>
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <button
            ref={el => {
              buttonRef.current = el;
              if (typeof ref === 'function') ref(el);
              else if (ref && 'current' in ref) ref.current = el;
            }}
            id={selectId}
            name={name}
            type="button"
            className={buttonClasses}
            onClick={() => {
              if (!disabled) {
                setIsOpen(!isOpen);
              }
            }}
            disabled={disabled}
            {...props}
          >
            <span
              className={`block truncate ${(value === undefined || value === null || (multiple && (!value || value.length === 0))) ? 'text-gray-500' : 'text-gray-900'}`}
            >
              {getSelectedDisplay()}
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg
                className={`h-5 w-5 text-gray-400 transform transition-transform duration-200 ${
                  isOpen ? 'rotate-180' : ''
                }`}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </button>

          {error && (
            <div className="absolute inset-y-0 right-8 pr-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-red-500"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          )}

          {/* Dropdown - Rendered via Portal with fixed positioning */}
          {isOpen && (
            <>
              {createPortal(
                <div
                  className="bg-white shadow-xl max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm border border-gray-200 dropdown-menu transition-all duration-200 ease-in-out transform"
                  style={{
                    ...dropdownStyle,
                    position: 'fixed',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    opacity: isOpen ? 1 : 0,
                    transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)',
                    zIndex: 9999,
                    overscrollBehavior: 'contain',
                  }}
                  data-dropdown
                  onWheel={e => e.stopPropagation()}
                  onScroll={e => e.stopPropagation()}
                >
                  {searchable && (
                    <div className="sticky top-0 bg-white px-3 py-2 border-b border-gray-200">
                      <input
                        ref={searchInputRef}
                        type="text"
                        className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-yellow focus:border-brand-yellow"
                        placeholder="Search options..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                      />
                    </div>
                  )}

                  {filteredOptions.length === 0 ? (
                    <div className="px-3 py-2 text-gray-500 text-sm">
                      {searchable && searchTerm
                        ? 'No options found'
                        : 'No options available'}
                    </div>
                  ) : (
                    filteredOptions.map(option => {
                      const isSelected = multiple
                        ? value && value.includes(option.value)
                        : value === option.value;

              return (
                        <div
                          key={option.value}
                          className={`cursor-pointer select-none relative py-1.5 pl-3 pr-9 hover:bg-brand-yellow/10 ${
                            isSelected
                              ? 'bg-brand-yellow/20 text-dark'
                              : 'text-gray-900'
                          }`}
                          onClick={() => handleOptionSelect(option.value)}
                        >
                          <div className="flex items-center">
                            {multiple && (
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                className="h-4 w-4 text-brand-yellow focus:ring-brand-yellow border-gray-300 rounded mr-3"
                              />
                            )}
                            <span
                              className={`block truncate ${isSelected ? 'font-medium' : 'font-normal'}`}
                            >
                              {option.label}
                            </span>
                          </div>

                          {isSelected && !multiple && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#fdbd0a]">
                              <svg
                                className="h-5 w-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>,
                document.body
              )}
            </>
          )}
        </div>

        {error && (
          <p className="mt-1 text-sm text-red-600" id={`${selectId}-error`}>
            {error}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

Select.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
  ]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  searchable: PropTypes.bool,
  multiple: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
};

export default Select;
